import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { PopulatedLead } from '../types';
import { useLeadAssigneesDropdown } from '../hooks/useLeadsDropdowns';
import { useFunnelsDropdown } from '@/modules/funnels/hooks/useFunnels';
import { useStepsByFunnelId } from '@/modules/funnels/hooks/useFunnelSteps';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Tabs,
  Button,
  Input,
  Textarea,
  Select,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

interface TransferLeadDialogProps {
  lead: PopulatedLead;
  onClose: () => void;
  onTransfer: (
    transferType: 'assignee' | 'funnel',
    data: {
      newAssigneeId?: string;
      newFunnelId?: string;
      newStepId?: string;
    }
  ) => Promise<void>;
  isLoading?: boolean;
}

interface Agent {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

/**
 * Dialog para transferir lead (Responsável ou Funil)
 *
 * Chrome = Modal shared (F72), abas = Tabs shared underline (F56):
 * - Responsável: Transferir para outro usuário
 * - Funil: Transferir para outro funil + etapa (selects dependentes)
 *
 * @example
 * ```tsx
 * <TransferLeadDialog
 *   lead={selectedLead}
 *   onClose={() => setIsTransferOpen(false)}
 *   onTransfer={handleTransferLead}
 * />
 * ```
 */
export const TransferLeadDialog: React.FC<TransferLeadDialogProps> = ({
  lead,
  onClose,
  onTransfer,
  isLoading = false,
}) => {
  const [transferType, setTransferType] = useState<'assignee' | 'funnel'>('assignee');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedFunnelId, setSelectedFunnelId] = useState<string>('');
  const [selectedStepId, setSelectedStepId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [notes, setNotes] = useState('');

  // Aba Responsável: só quem participa do funil do lead (qualquer papel) —
  // mesma régua que o servidor confere na transferência.
  const { data: usersData = [], isLoading: isLoadingUsers } = useLeadAssigneesDropdown(
    lead.funnelId || lead.funnel?.id || undefined,
  );

  // Fetch funnels for funnel tab
  const { data: funnelsData = [], isLoading: isLoadingFunnels } = useFunnelsDropdown();

  // Fetch steps when funnel is selected (dependente)
  const { data: stepsData, isLoading: isLoadingSteps } = useStepsByFunnelId(
    selectedFunnelId || undefined
  );

  // Reset stepId when funnel changes
  useEffect(() => {
    setSelectedStepId('');
  }, [selectedFunnelId]);

  // Transform users data to agent format
  const agents: Agent[] = useMemo(() => {
    return usersData.map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email ?? '',
      avatar: user.avatar,
    }));
  }, [usersData]);

  // Filter agents by search term
  const filteredAgents = agents.filter(
    (agent) =>
      agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  const handleTransfer = async () => {
    if (transferType === 'assignee') {
      if (!selectedUserId) {
        toast.error('Selecione um responsável');
        return;
      }
      if (selectedUserId === lead.assigneeId) {
        toast.error('O lead já está atribuído a este usuário');
        return;
      }
      await onTransfer('assignee', { newAssigneeId: selectedUserId });
    } else {
      if (!selectedFunnelId || !selectedStepId) {
        toast.error('Selecione um funil e uma etapa');
        return;
      }
      await onTransfer('funnel', {
        newFunnelId: selectedFunnelId,
        newStepId: selectedStepId,
      });
    }
  };

  const isTransferDisabled = () => {
    if (transferType === 'assignee') {
      return isLoading || !selectedUserId;
    } else {
      return isLoading || !selectedFunnelId || !selectedStepId;
    }
  };

  const assigneeTabContent = (
    <div className="pt-4">
      {/* Info do lead atual */}
      <div
        className="p-3 rounded-lg mb-4"
        style={{
          backgroundColor: '#f1f5f9',
          borderColor: '#e5e7eb',
        }}
      >
        <p className="text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
          Lead: {lead.contact?.name || 'Sem nome'}
        </p>
        {lead.assignee && (
          <p className="text-xs" style={{ color: '#64748b' }}>
            Responsável atual: {lead.assignee.name}
          </p>
        )}
      </div>

      {/* Search */}
      <div className="mb-4">
        <Input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar responsável..."
          icon={'search'}
          data-testid="lead-transfer-user-search"
        />
      </div>

      {/* Users List */}
      <div className="space-y-2 max-h-[45vh] overflow-y-auto">
        {isLoadingUsers ? (
          <div className="text-center py-8">
            <div className="flex items-center justify-center gap-2" style={{ color: '#64748b' }}>
              <Icon name="loader" className="w-5 h-5 animate-spin" />
              <p>Carregando usuários...</p>
            </div>
          </div>
        ) : filteredAgents.length === 0 ? (
          <div className="text-center py-8">
            <p style={{ color: '#64748b' }}>
              {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário disponível'}
            </p>
          </div>
        ) : (
          <div style={{ border: '1px solid #eef1f5', borderRadius: 10, overflow: 'hidden' }}>
            {filteredAgents.map((agent, index) => (
              <button
                key={agent.id}
                data-testid={`lead-transfer-user-option-${agent.id}`}
                onClick={() => setSelectedUserId(agent.id)}
                className="w-full text-left flex items-center gap-[11px] transition-colors"
                style={{
                  padding: '10px 12px',
                  borderBottom:
                    index < filteredAgents.length - 1 ? '1px solid #f1f5f9' : 'none',
                  backgroundColor:
                    selectedUserId === agent.id ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (selectedUserId !== agent.id) {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedUserId !== agent.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {/* Radio */}
                <div
                  className="w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0"
                  style={{
                    borderColor: selectedUserId === agent.id ? '#8b5cf6' : '#cbd5e1',
                  }}
                >
                  {selectedUserId === agent.id && (
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: '#8b5cf6' }}
                    />
                  )}
                </div>

                {/* Avatar */}
                <Avatar className="w-[30px] h-[30px]" isSquircle>
                  <AvatarImage src={agent.avatar} alt={agent.name} isSquircle />
                  <AvatarFallback isSquircle>
                    <span className="text-indigo-600 font-semibold">
                      {agent.name.charAt(0).toUpperCase()}
                    </span>
                  </AvatarFallback>
                </Avatar>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold truncate" style={{ color: '#1e293b' }}>
                    {agent.name}
                  </h4>
                  <p className="text-xs truncate" style={{ color: '#64748b' }}>
                    {agent.email}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const funnelTabContent = (
    <div className="pt-4">
      {/* Info do funil/etapa atual */}
      <div
        className="p-3 rounded-lg mb-4"
        style={{
          backgroundColor: '#f1f5f9',
          borderColor: '#e5e7eb',
        }}
      >
        <p className="text-sm font-medium mb-1" style={{ color: '#1e293b' }}>
          Lead: {lead.contact?.name || 'Sem nome'}
        </p>
        <p className="text-xs" style={{ color: '#64748b' }}>
          Funil atual: {lead.step?.name || 'Nenhum funil/etapa atribuído'}
        </p>
      </div>

      {/* Select Funil */}
      <div className="mb-4">
        <Select
          label="Novo Funil"
          value={selectedFunnelId}
          onChange={(val) => setSelectedFunnelId(val)}
          placeholder="Selecione um funil"
          options={funnelsData.map((funnel) => ({
            value: funnel.id,
            label: funnel.name
          }))}
          disabled={isLoadingFunnels || isLoading}
        />
      </div>

      {/* Select Etapa (dependente) */}
      {selectedFunnelId && (
        <div className="mb-4">
          {isLoadingSteps ? (
            <div className="flex items-center gap-2 px-4 py-2" style={{ color: '#64748b' }}>
              <Icon name="loader" className="w-4 h-4 animate-spin" />
              <span className="text-sm">Carregando etapas...</span>
            </div>
          ) : (
            <Select
              label="Nova Etapa"
              value={selectedStepId}
              onChange={(val) => setSelectedStepId(val)}
              placeholder="Selecione uma etapa"
              options={stepsData?.map((step) => ({
                value: step.id,
                label: step.name
              })) || []}
              disabled={isLoading}
            />
          )}
        </div>
      )}

      {/* Info Box */}
      <div
        className="p-3 rounded-lg border"
        style={{
          backgroundColor: '#EFF6FF',
          borderColor: '#BFDBFE',
        }}
      >
        <p className="text-xs" style={{ color: '#1E40AF' }}>
          <span className="font-semibold">Atenção:</span> Ao transferir este lead para
          outro funil, ele será movido para a etapa selecionada. As atividades e histórico
          serão mantidos.
        </p>
      </div>
    </div>
  );

  return (
    <Modal isOpen onClose={handleClose} maxWidth="3xl">
      <div data-testid="lead-transfer-dialog">
        <ModalHeader onClose={handleClose}>
          <div
            className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-none"
            style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}
          >
            <Icon name="refresh" className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold truncate" style={{ color: '#1e293b' }}>
              Transferir Lead
            </h2>
            {lead.contact?.name && (
              <p className="text-sm truncate" style={{ color: '#64748b' }}>
                {lead.contact.name}
              </p>
            )}
          </div>
        </ModalHeader>

        <ModalContent>
          {/* Abas — Tabs shared underline (F56) */}
          <Tabs
            variant="underline"
            activeTab={transferType}
            onChange={(tabId) => {
              if (isLoading) return;
              if (tabId === 'assignee') {
                setTransferType('assignee');
                setSelectedUserId('');
              } else {
                setTransferType('funnel');
                setSelectedFunnelId('');
                setSelectedStepId('');
              }
            }}
            tabs={[
              { id: 'assignee', label: 'Responsável', icon: 'user', content: assigneeTabContent },
              { id: 'funnel', label: 'Funil', icon: 'trending-up', content: funnelTabContent },
            ]}
          />

          {/* Notes (compartilhado por ambas as abas) */}
          <div className="mt-4">
            <Textarea
              id="transfer-notes"
              label="Observações (opcional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione observações..."
              rows={3}
              resize="none"
            />
          </div>
        </ModalContent>

        <ModalFooter>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
            data-testid="lead-transfer-cancel"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon="refresh"
            loading={isLoading}
            onClick={handleTransfer}
            disabled={isTransferDisabled()}
            data-testid="lead-transfer-confirm"
          >
            {isLoading ? 'Transferindo...' : 'Transferir Lead'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};
